import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { FiltroUnidades } from "../filtros/FiltroUnidades";
import { FormatoSalida } from "./forms/FormatoSalida";
import { FormatoCheckPruebas } from "./forms/FormatoCheckPruebas";
import { FormatoDetalleSalida } from "./forms/FormatoDetalleSalida";
import { useUnidades } from "../../hooks/test/useUnidades";
import { cerrarTodo } from "../../helpers/test/generalesHelper";
import { useGeneral } from "../../hooks/test/useGeneral";
import { Spinner } from "../Spinner";
import { SocketContext } from "../../context/SocketContext";
import { PaginatedItems } from "../customComponents/Pagination";
import { TituloUnidadesConteo } from "../customFunctionalComponents/components/TituloUnidadesConteo";
import { SinUnidades } from "../customFunctionalComponents/components/SinUnidades";
import { TarjetaUnidades } from "../customFunctionalComponents/components/TarjetaUnidades";
import { AuthContext } from "../../context/authContext/authContext";

export const ListadoUnidades = ({ busqueda }) => {
  const { socket } = useContext(SocketContext);
  const [unidad, setUnidad] = useState({});
  const [unidades, setUnidades] = useState([]);
  const [viaje, setViaje] = useState({});
  const [clear, setClear] = useState(false);
  const { getUnidad, getUnidadesFiltro, getViajeUnidad } = useUnidades();
  const { openSidebar } = useGeneral();
  const [loading, setLoading] = useState(false);
  const [numeroPagina, setNumeroPagina] = useState(1);
  const [palabra, setPalabra] = useState("");
  const [paginado, setPaginado] = useState({ totalPaginas: 0, pagina: 1 });
  const { user, negocioSeleccionado } = useContext(AuthContext);

  // 🔥 Se reemplaza arrayCedis state por un ref seguro
  const arrayCedisRef = useRef([]);

  // Evitar race conditions
  const lastCallRef = useRef();

  const getViajeIdUnidad = async (id_unidad) => {
    let resp = await getViajeUnidad(id_unidad);
    if (resp.ok) setViaje(resp);
  };

  const selectUnidad = async (id_unidad) => {
    setClear(true);
    cerrarTodo();
    setLoading(true);
    let resp = await getUnidad(id_unidad);

    if (resp.ok) {
      // openSidebar(resp.unidad.status_id);

      setUnidad({ ...resp.unidad });

      if (resp.unidad.status_id !== 1) {
        getViajeIdUnidad(resp.unidad.unidad_id);
      }
    }
    setLoading(false);
  };

  // 🚫 Ya NO limpiamos arrayCedis (eso causaba loops)
  const cleanUp = () => {
    setUnidad(null);
    setUnidades([]);
    setPaginado({ totalPaginas: 0, pagina: 1 });
    arrayCedisRef.current = []; // SEGURO (sin re-render)
    setLoading(false);
  };

  // ✔️ loadUnidades ya no depende de arrayCedis → estable
  const loadUnidades = useCallback(
    async (page = numeroPagina) => {
      setLoading(true);
      const callId = Symbol();
      lastCallRef.current = callId;

      const resp = await getUnidadesFiltro({
        busqueda,
        numeroPagina: page,
        search: palabra,
        arrayCedis:
          arrayCedisRef.current.length > 0
            ? { arrayCedis: arrayCedisRef.current }
            : [],
      });

      if (lastCallRef.current !== callId) return;

      if (!resp.ok) {
        cleanUp();
        return setLoading(false);
      }

      setUnidades(resp.data?.unidades ?? []);
      setPaginado(resp.data?.paginacion ?? { totalPaginas: 0, pagina: 1 });
      setLoading(false);
    },
    [palabra, numeroPagina, busqueda]
  );

  // ✔️ Recarga cuando cambia palabra (arrayCedis YA NO dispara bucle)
  useEffect(() => {
    setNumeroPagina(1);
    loadUnidades(1);
  }, [palabra]);

  // ✔️ socket listener estable
  useEffect(() => {
    if (!socket) return;
    const refresh = () => {
      if (palabra.trim() !== "") loadUnidades(1);
      else loadUnidades(numeroPagina);
    }

    socket.on("new_event_robo_", async (data) => {
      console.log("new event robo actualizando Unidades");
      if (data.idNegocio === user.negocio_id || data.idNegocio === parseInt(negocioSeleccionado)) loadUnidades(numeroPagina);
    });

    socket.on("unidades_programar_", refresh);

    return () => {
      socket.off("unidades_programar_", refresh);
      socket.off('new_event_robo_');
    };
  }, [socket, palabra, numeroPagina, loadUnidades]);

  useEffect(() => {
    if (unidad?.status_id) {
      // Esperar un ciclo para que React pinte los componentes
      setTimeout(() => openSidebar(unidad.status_id), 0);
    }
  }, [unidad]);

  const handlePageChange = (page) => {
    setNumeroPagina(page);
    loadUnidades(page);
  };

  // Cuando usuario cambia filtros de CEDIS
  const handleChange = (campo, valor) => {
    if (campo === "palabra") {
      return setPalabra(valor);
    }

    if (campo === "arrayCedis") {
      arrayCedisRef.current = valor; // actualizar SOLO ref (sin render extra)
      loadUnidades(1); // recargar manual
    }
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="row">
        <div id="columna1">
          <FiltroUnidades onChange={handleChange} />

          <TituloUnidadesConteo
            longitud={paginado.totalRegistros}
          />

          <div className="col-12 col-sm-10 col-md-12 col-lg-12 col-xl-10 mx-auto">
            <SinUnidades longitud={unidades.length} />
            {
              !loading && <PaginatedItems
                paginacion={paginado || { totalPaginas: 0, pagina: 1 }}
                onPageChange={handlePageChange}
                items={(unidades || []).map((unidad) => {
                  const {
                    unidad_id,
                    unidad_numero,
                    status_id,
                    cedi,
                    status,
                    spot,
                    nombre_transportadora,
                    cat_tipos_almacen = "",
                    comands_logs,
                    tipo_unidad_id,
                  } = unidad;

                  return (
                    <TarjetaUnidades
                      status_id={status_id}
                      selectUnidad={selectUnidad}
                      unidad_id={unidad_id}
                      unidad_numero={unidad_numero}
                      cat_tipos_almacen={cat_tipos_almacen}
                      cedi={cedi}
                      spot={spot}
                      nombre_transportadora={nombre_transportadora}
                      unidad={unidad}
                      status={status}
                      comandos={comands_logs}
                      tipo_unidad_id={tipo_unidad_id}
                    />
                  );
                })}
              />
            }

          </div>
        </div>

        {
          unidad && (
            <>
              <div className="display-oculto col-3" id="myCol">
                <FormatoSalida
                  unidad={unidad}
                  getUnidades={loadUnidades}
                  clear={clear}
                  setClear={setClear}
                />
              </div>

              <div className="display-oculto col-3" id="pruebas">
                <FormatoCheckPruebas
                  unidad={unidad}
                  getUnidades={loadUnidades}
                  clear={clear}
                  setClear={setClear}
                  viaje={viaje}
                />
              </div>

              <div className="display-oculto" id="detalle">
                <FormatoDetalleSalida
                  unidad={unidad}
                  getUnidades={loadUnidades}
                  clear={clear}
                  setClear={setClear}
                  viaje={viaje}
                />
              </div>
            </>
          )
        }
      </div>
    </>
  );
};

